import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Copy, Edit3, Eye, Play, Sparkles, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = ['Finance', 'Marketing', 'HR', 'Strategy', 'Operations'];
const TEMPLATES = [
  { id: 'decision_lab', label: 'Decision Lab' },
  { id: 'resource_allocation', label: 'Resource Allocation Lab' },
  { id: 'dashboard', label: 'Dashboard Decision Lab' },
  { id: 'multi_round', label: 'Multi-Round Business Sim' },
];

const emptyDecision = () => ({
  id: crypto.randomUUID(),
  question: '',
  decision_type: 'multiple_choice',
  choices: [],
  min_value: 0,
  max_value: 100,
  default_value: 0,
  effect_variable: '',
  effect_multiplier: 0,
});

const emptyRound = () => ({
  id: crypto.randomUUID(),
  title: 'New Round',
  scenario: '',
  decisions: [],
  rules: [],
});

const emptyVar = () => ({ name: 'NewVariable', starting_value: 0, minimum: null, maximum: null, unit: '', visible: true, description: '' });
const emptyChoice = () => ({ id: crypto.randomUUID(), label: '', feedback: '', variable_effects: {} });
const emptyRule = () => ({ id: crypto.randomUUID(), if_variable: '', operator: '>', value: 0, then_effects: {}, narrative: '' });
const emptyScoring = () => ({ id: crypto.randomUUID(), name: 'Financial Performance', weight: 1, variable_name: '', higher_is_better: true, benchmark: 100 });

const blankSim = () => ({
  title: '', description: '', subject: 'Strategy', template_type: 'decision_lab',
  learner_role: '', learning_objectives: [], duration_minutes: 30, difficulty: 'intermediate',
  published: false, linked_case_id: null,
  variables: [], rounds: [], scoring: [], debrief: '',
});

export default function SimulationStudio() {
  const [sims, setSims] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // AI Copilot state
  const [showAiModal, setShowAiModal] = useState(false);
  const [cases, setCases] = useState([]);
  const [aiCaseId, setAiCaseId] = useState('');
  const [aiHint, setAiHint] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]); // array of case ids
  const [bulkJobs, setBulkJobs] = useState([]); // [{case_id, case_title, job_id, status, draft?, error?}]
  const [bulkPolling, setBulkPolling] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/simulations`);
      setSims(res.data || []);
    } catch {
      toast.error('Could not load simulations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openAiModal = async () => {
    setShowAiModal(true);
    setAiCaseId('');
    setAiHint('');
    setBulkMode(false);
    setBulkSelected([]);
    setBulkJobs([]);
    if (cases.length === 0) {
      try {
        const res = await axios.get(`${API}/cases`);
        setCases(res.data || []);
      } catch { toast.error('Could not load cases'); }
    }
  };

  const toggleBulkCase = (id) => {
    setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const startBulkGenerate = async () => {
    if (bulkSelected.length === 0) { toast.error('Pick at least one case'); return; }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/simulations/ai-generate-bulk`, { case_ids: bulkSelected, hint: aiHint });
      const initial = res.data.jobs.map(j => ({ ...j, status: 'pending' }));
      setBulkJobs(initial);
      setBulkPolling(true);
      pollBulkJobs(initial);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk generation failed to start');
      setAiGenerating(false);
    }
  };

  const pollBulkJobs = async (jobs) => {
    const started = Date.now();
    const maxWait = 10 * 60 * 1000; // 10 min
    while (Date.now() - started < maxWait) {
      await new Promise(r => setTimeout(r, 5000));
      const updates = await Promise.all(jobs.map(async (j) => {
        if (j.status === 'ready' || j.status === 'failed') return j;
        try {
          const s = await axios.get(`${API}/admin/simulations/ai-generate/status/${j.job_id}`);
          return { ...j, status: s.data.status, draft: s.data.draft, error: s.data.error };
        } catch { return j; }
      }));
      setBulkJobs(updates);
      if (updates.every(u => u.status === 'ready' || u.status === 'failed')) {
        setBulkPolling(false);
        setAiGenerating(false);
        const ready = updates.filter(u => u.status === 'ready').length;
        toast.success(`Bulk generation complete: ${ready}/${updates.length} drafts ready. Email sent.`);
        return;
      }
    }
    setBulkPolling(false);
    setAiGenerating(false);
    toast.error('Bulk generation timed out — some jobs may still complete in the background');
  };

  const openBulkDraft = (job) => {
    if (!job.draft) return;
    setShowAiModal(false);
    setSelected(job.draft);
  };

  const generateFromCase = async () => {
    if (!aiCaseId) { toast.error('Please pick a case'); return; }
    setAiGenerating(true);
    try {
      const startRes = await axios.post(`${API}/admin/simulations/ai-generate`, { case_id: aiCaseId, hint: aiHint });
      const jobId = startRes.data.job_id;
      // Poll every 5s (up to 3 min)
      const startedAt = Date.now();
      const maxWait = 180000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise(r => setTimeout(r, 5000));
        const statusRes = await axios.get(`${API}/admin/simulations/ai-generate/status/${jobId}`);
        const { status, draft, error } = statusRes.data;
        if (status === 'ready') {
          toast.success('Draft ready — review and save when you are happy with it.');
          setShowAiModal(false);
          setSelected(draft);
          return;
        }
        if (status === 'failed') {
          toast.error(error || 'AI generation failed');
          return;
        }
        if (Date.now() - startedAt > maxWait) {
          toast.error('Generation is taking too long. Please try again with a shorter hint.');
          return;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const startNew = () => setSelected(blankSim());

  const save = async () => {
    if (!selected?.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (selected.id) {
        const res = await axios.put(`${API}/admin/simulations/${selected.id}`, selected);
        saved = res.data;
      } else {
        const res = await axios.post(`${API}/admin/simulations`, selected);
        saved = res.data;
      }
      toast.success('Simulation saved');
      setSelected(saved);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this simulation?')) return;
    try {
      await axios.delete(`${API}/admin/simulations/${id}`);
      toast.success('Deleted');
      if (selected?.id === id) setSelected(null);
      await refresh();
    } catch { toast.error('Delete failed'); }
  };

  const duplicate = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/simulations/${id}/duplicate`);
      toast.success('Duplicated');
      setSelected(res.data);
      await refresh();
    } catch { toast.error('Duplicate failed'); }
  };

  const updateField = (key, val) => setSelected(s => ({ ...s, [key]: val }));

  if (!selected) {
    return (
      <div data-testid="studio-list-view">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-serif font-semibold text-slate-900">Simulation Studio</h3>
            <p className="text-sm text-slate-500">Build no-code interactive decision labs from your cases.</p>
          </div>
          <button onClick={startNew} className="inline-flex items-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800" data-testid="studio-new-btn">
            <Plus className="w-4 h-4" /> New Simulation
          </button>
          <button onClick={openAiModal} className="inline-flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 ml-2" data-testid="studio-ai-generate-btn">
            <Sparkles className="w-4 h-4" /> Generate from Case
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>
        ) : sims.length === 0 ? (
          <div className="text-center py-12 bg-white border border-dashed border-slate-300 rounded-xl">
            <p className="text-slate-700 font-medium">No simulations yet.</p>
            <p className="text-slate-500 text-sm mt-1">Click &quot;New Simulation&quot; to build your first one.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3">Title</th>
                  <th className="text-left px-4 py-3">Subject</th>
                  <th className="text-left px-4 py-3">Template</th>
                  <th className="text-left px-4 py-3">Rounds</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sims.map(s => (
                  <tr key={s.id} data-testid={`sim-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{s.title}</td>
                    <td className="px-4 py-3">{s.subject}</td>
                    <td className="px-4 py-3">{s.template_type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3">{s.rounds?.length || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${s.published ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{s.published ? 'Published' : 'Draft'}</span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button onClick={() => setSelected(s)} className="px-3 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                      <a href={`/simulations/${s.id}/play`} target="_blank" rel="noreferrer" className="px-3 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1"><Play className="w-3 h-3" /> Play</a>
                      <button onClick={() => duplicate(s.id)} className="px-3 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                      <button onClick={() => remove(s.id)} className="px-3 py-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AI Copilot Modal */}
        {showAiModal && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4" data-testid="ai-modal">
            <div className="bg-white rounded-xl max-w-3xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
              <button onClick={() => setShowAiModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-serif font-semibold text-slate-900">Generate simulation{bulkMode ? 's' : ''} from case{bulkMode ? 's' : ''}</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Claude Sonnet 4.5 drafts a full decision-lab simulation from case content. You review and edit before publishing — nothing is saved automatically.</p>

              {/* Mode toggle */}
              <div className="flex items-center gap-2 mb-4 p-1 bg-slate-100 rounded-lg w-fit">
                <button onClick={() => { setBulkMode(false); setBulkJobs([]); }} disabled={bulkPolling} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${!bulkMode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-testid="ai-mode-single">Single case</button>
                <button onClick={() => { setBulkMode(true); setAiCaseId(''); }} disabled={bulkPolling} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${bulkMode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-testid="ai-mode-bulk">Bulk (up to 20)</button>
              </div>

              {/* Progress panel when bulk polling / done */}
              {bulkJobs.length > 0 ? (
                <div className="space-y-3" data-testid="bulk-progress-panel">
                  <div className="text-sm text-slate-600">
                    {bulkPolling ? '⏳ Generating drafts in parallel — email will be sent when all are ready.' : '✅ Bulk generation complete. Click any ready draft to review.'}
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr><th className="text-left px-3 py-2">Case</th><th className="text-left px-3 py-2 w-32">Status</th><th className="text-right px-3 py-2 w-24">Action</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {bulkJobs.map(j => (
                          <tr key={j.job_id} data-testid={`bulk-row-${j.case_id}`}>
                            <td className="px-3 py-2 text-slate-800 truncate max-w-md">{j.case_title}</td>
                            <td className="px-3 py-2">
                              {j.status === 'pending' && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><div className="animate-spin rounded-full h-3 w-3 border border-slate-300 border-t-slate-700" /> Generating...</span>}
                              {j.status === 'ready' && <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">✅ Ready</span>}
                              {j.status === 'failed' && <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded" title={j.error || ''}>❌ Failed</span>}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {j.status === 'ready' && <button onClick={() => openBulkDraft(j)} className="text-xs text-orange-600 hover:text-orange-700 font-medium" data-testid={`open-draft-${j.case_id}`}>Open draft →</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setBulkJobs([]); setBulkSelected([]); }} disabled={bulkPolling} className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50">Start over</button>
                    <button onClick={() => setShowAiModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50">Close</button>
                  </div>
                </div>
              ) : !bulkMode ? (
                // Single-case form
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Case</label>
                    <select value={aiCaseId} onChange={(e) => setAiCaseId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="ai-case-select">
                      <option value="">— Select a case —</option>
                      {cases.map(c => <option key={c.id} value={c.id}>{c.title}{c.year ? ` (${c.year})` : ''}</option>)}
                    </select>
                    {cases.length === 0 && <p className="text-xs text-slate-400 mt-1">Loading cases...</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Design hint <span className="text-slate-400">(optional)</span></label>
                    <textarea rows={3} value={aiHint} onChange={(e) => setAiHint(e.target.value)} placeholder="e.g. Focus on ethical decision-making. Make it 4 rounds." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="ai-hint-input" />
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-900">
                    💡 Takes 30–90 seconds. You&apos;ll land in the Studio editor with a full draft prefilled — variables, rounds, decisions, rules, scoring, debrief.
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowAiModal(false)} disabled={aiGenerating} className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
                    <button onClick={generateFromCase} disabled={aiGenerating || !aiCaseId} className="inline-flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 disabled:opacity-50" data-testid="ai-generate-submit-btn">
                      {aiGenerating ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Generating draft...</>) : (<><Sparkles className="w-4 h-4" /> Generate draft</>)}
                    </button>
                  </div>
                </div>
              ) : (
                // Bulk multi-select form
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-slate-700">Select cases ({bulkSelected.length} selected, max 20)</label>
                      {bulkSelected.length > 0 && <button onClick={() => setBulkSelected([])} className="text-xs text-slate-500 hover:text-slate-900">Clear</button>}
                    </div>
                    <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto" data-testid="bulk-case-list">
                      {cases.length === 0 ? (
                        <div className="p-4 text-xs text-slate-400">Loading cases...</div>
                      ) : cases.map(c => (
                        <label key={c.id} className={`flex items-start gap-3 p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${bulkSelected.includes(c.id) ? 'bg-orange-50' : ''}`}>
                          <input type="checkbox" checked={bulkSelected.includes(c.id)} onChange={() => toggleBulkCase(c.id)} disabled={!bulkSelected.includes(c.id) && bulkSelected.length >= 20} className="mt-1" data-testid={`bulk-check-${c.id}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-800 truncate">{c.title}</div>
                            <div className="text-xs text-slate-500">{c.year ? `${c.year} • ` : ''}{Array.isArray(c.domain) ? c.domain.join(', ') : c.domain || ''}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Design hint <span className="text-slate-400">(optional, applied to all)</span></label>
                    <textarea rows={2} value={aiHint} onChange={(e) => setAiHint(e.target.value)} placeholder="e.g. 3 rounds each. Focus on strategic trade-offs." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-900">
                    💡 We&apos;ll generate all drafts in parallel (~1-2 min total for 5-10 cases). You can close this browser and come back — we&apos;ll email <strong>{cases.length > 0 ? 'you' : 'the admin'}</strong> when everything is ready.
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowAiModal(false)} disabled={aiGenerating} className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
                    <button onClick={startBulkGenerate} disabled={aiGenerating || bulkSelected.length === 0} className="inline-flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 disabled:opacity-50" data-testid="bulk-generate-btn">
                      {aiGenerating ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Starting...</>) : (<><Sparkles className="w-4 h-4" /> Generate {bulkSelected.length} draft{bulkSelected.length === 1 ? '' : 's'}</>)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Editor
  return (
    <div data-testid="studio-editor-view" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => setSelected(null)} className="text-sm text-slate-600 hover:text-slate-900 mb-1">← Back to list</button>
          <h3 className="text-lg font-serif font-semibold text-slate-900">{selected.id ? 'Edit' : 'New'} Simulation</h3>
        </div>
        <div className="flex gap-2">
          {selected.id && (
            <a href={`/simulations/${selected.id}/play`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"><Eye className="w-4 h-4" /> Preview</a>
          )}
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50" data-testid="studio-save-btn">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      {/* Basics */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="font-serif font-semibold text-slate-900 mb-4">Basics</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input type="text" value={selected.title} onChange={e => updateField('title', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="e.g., Johnson & Johnson Strategic Dilemma" data-testid="studio-title-input" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea rows={3} value={selected.description} onChange={e => updateField('description', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
            <select value={selected.subject} onChange={e => updateField('subject', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
              {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Template</label>
            <select value={selected.template_type} onChange={e => updateField('template_type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
              {TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Learner role</label>
            <input type="text" value={selected.learner_role} onChange={e => updateField('learner_role', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="e.g., You are the CEO of..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty</label>
            <select value={selected.difficulty} onChange={e => updateField('difficulty', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Duration (min)</label>
            <input type="number" value={selected.duration_minutes} onChange={e => updateField('duration_minutes', Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Linked Case ID (optional)</label>
            <input type="text" value={selected.linked_case_id || ''} onChange={e => updateField('linked_case_id', e.target.value || null)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="Case UUID" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Learning objectives (one per line)</label>
            <textarea rows={3} value={(selected.learning_objectives || []).join('\n')} onChange={e => updateField('learning_objectives', e.target.value.split('\n').map(x => x.trim()).filter(Boolean))} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!selected.published} onChange={e => updateField('published', e.target.checked)} data-testid="studio-published-checkbox" />
            Published (visible to learners)
          </label>
        </div>
      </div>

      {/* Variables */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-serif font-semibold text-slate-900">Variables</h4>
          <button onClick={() => updateField('variables', [...selected.variables, emptyVar()])} className="px-3 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add variable</button>
        </div>
        {selected.variables.length === 0 ? (
          <p className="text-sm text-slate-500">Add variables like Cash, Market Share, Morale etc. that change through the simulation.</p>
        ) : (
          <div className="space-y-2">
            {selected.variables.map((v, i) => (
              <div key={i} className="grid grid-cols-2 md:grid-cols-7 gap-2 items-center">
                <input value={v.name} onChange={e => { const arr = [...selected.variables]; arr[i] = { ...v, name: e.target.value }; updateField('variables', arr); }} placeholder="Name" className="px-2 py-1.5 border border-slate-300 rounded text-sm md:col-span-2" />
                <input type="number" value={v.starting_value} onChange={e => { const arr = [...selected.variables]; arr[i] = { ...v, starting_value: Number(e.target.value) }; updateField('variables', arr); }} placeholder="Start" className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
                <input type="number" value={v.minimum ?? ''} onChange={e => { const arr = [...selected.variables]; arr[i] = { ...v, minimum: e.target.value === '' ? null : Number(e.target.value) }; updateField('variables', arr); }} placeholder="Min" className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
                <input type="number" value={v.maximum ?? ''} onChange={e => { const arr = [...selected.variables]; arr[i] = { ...v, maximum: e.target.value === '' ? null : Number(e.target.value) }; updateField('variables', arr); }} placeholder="Max" className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
                <input value={v.unit} onChange={e => { const arr = [...selected.variables]; arr[i] = { ...v, unit: e.target.value }; updateField('variables', arr); }} placeholder="Unit (%, ₹Cr)" className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
                <button onClick={() => { const arr = selected.variables.filter((_, idx) => idx !== i); updateField('variables', arr); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rounds */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-serif font-semibold text-slate-900">Rounds</h4>
          <button onClick={() => updateField('rounds', [...selected.rounds, emptyRound()])} className="px-3 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add round</button>
        </div>
        {selected.rounds.length === 0 && <p className="text-sm text-slate-500">Add rounds with scenarios and decisions.</p>}
        <div className="space-y-6">
          {selected.rounds.map((r, ri) => (
            <div key={r.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase font-mono text-slate-500">Round {ri + 1}</div>
                <button onClick={() => { const arr = selected.rounds.filter((_, idx) => idx !== ri); updateField('rounds', arr); }} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
              <input value={r.title} onChange={e => { const arr = [...selected.rounds]; arr[ri] = { ...r, title: e.target.value }; updateField('rounds', arr); }} placeholder="Round title" className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm mb-2 bg-white" />
              <textarea rows={2} value={r.scenario} onChange={e => { const arr = [...selected.rounds]; arr[ri] = { ...r, scenario: e.target.value }; updateField('rounds', arr); }} placeholder="Scenario narrative for this round..." className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white mb-3" />

              {/* Decisions inside round */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase font-mono text-slate-500">Decisions</div>
                  <button onClick={() => { const arr = [...selected.rounds]; arr[ri] = { ...r, decisions: [...r.decisions, emptyDecision()] }; updateField('rounds', arr); }} className="px-2 py-0.5 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1 bg-white"><Plus className="w-3 h-3" /> Add decision</button>
                </div>
                {r.decisions.map((d, di) => (
                  <div key={d.id} className="bg-white border border-slate-200 rounded p-3 mb-2">
                    <div className="flex items-start gap-2 mb-2">
                      <select value={d.decision_type} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; ds[di] = { ...d, decision_type: e.target.value }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} className="px-2 py-1 border border-slate-300 rounded text-xs">
                        <option value="multiple_choice">Multiple choice</option>
                        <option value="numeric">Numeric</option>
                        <option value="slider">Slider</option>
                      </select>
                      <input value={d.question} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; ds[di] = { ...d, question: e.target.value }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="Question shown to learner" className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm" />
                      <button onClick={() => { const arr = [...selected.rounds]; const ds = r.decisions.filter((_, idx) => idx !== di); arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {d.decision_type === 'multiple_choice' ? (
                      <div className="pl-3 border-l-2 border-slate-200 space-y-2">
                        {d.choices.map((c, ci) => (
                          <div key={c.id} className="space-y-1 pb-2">
                            <div className="flex items-center gap-2">
                              <input value={c.label} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; const cs = [...d.choices]; cs[ci] = { ...c, label: e.target.value }; ds[di] = { ...d, choices: cs }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="Choice label" className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm" />
                              <button onClick={() => { const arr = [...selected.rounds]; const ds = [...r.decisions]; const cs = d.choices.filter((_, idx) => idx !== ci); ds[di] = { ...d, choices: cs }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-3 h-3" /></button>
                            </div>
                            <input value={c.feedback || ''} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; const cs = [...d.choices]; cs[ci] = { ...c, feedback: e.target.value }; ds[di] = { ...d, choices: cs }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="Feedback shown after choosing (optional)" className="w-full px-2 py-1 border border-slate-200 rounded text-xs bg-slate-50" />
                            <input value={Object.entries(c.variable_effects || {}).map(([k, v]) => `${k}:${v}`).join(', ')} onChange={e => { const effects = {}; e.target.value.split(',').forEach(pair => { const [k, v] = pair.split(':').map(x => x?.trim()); if (k && v !== undefined && !isNaN(Number(v))) effects[k] = Number(v); }); const arr = [...selected.rounds]; const ds = [...r.decisions]; const cs = [...d.choices]; cs[ci] = { ...c, variable_effects: effects }; ds[di] = { ...d, choices: cs }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="Effects e.g. Cash:-10, MarketShare:+5" className="w-full px-2 py-1 border border-slate-200 rounded text-xs" />
                          </div>
                        ))}
                        <button onClick={() => { const arr = [...selected.rounds]; const ds = [...r.decisions]; ds[di] = { ...d, choices: [...d.choices, emptyChoice()] }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} className="text-xs text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add choice</button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-2">
                        <input type="number" value={d.min_value ?? ''} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; ds[di] = { ...d, min_value: Number(e.target.value) }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="Min" className="px-2 py-1 border border-slate-300 rounded text-xs" />
                        <input type="number" value={d.max_value ?? ''} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; ds[di] = { ...d, max_value: Number(e.target.value) }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="Max" className="px-2 py-1 border border-slate-300 rounded text-xs" />
                        <input value={d.effect_variable} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; ds[di] = { ...d, effect_variable: e.target.value }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="Var name" className="px-2 py-1 border border-slate-300 rounded text-xs" />
                        <input type="number" step="0.01" value={d.effect_multiplier ?? 0} onChange={e => { const arr = [...selected.rounds]; const ds = [...r.decisions]; ds[di] = { ...d, effect_multiplier: Number(e.target.value) }; arr[ri] = { ...r, decisions: ds }; updateField('rounds', arr); }} placeholder="× multiplier" className="px-2 py-1 border border-slate-300 rounded text-xs" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Rules */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase font-mono text-slate-500">Rules (IF/THEN)</div>
                  <button onClick={() => { const arr = [...selected.rounds]; arr[ri] = { ...r, rules: [...r.rules, emptyRule()] }; updateField('rounds', arr); }} className="px-2 py-0.5 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1 bg-white"><Plus className="w-3 h-3" /> Add rule</button>
                </div>
                {r.rules.map((rule, rui) => (
                  <div key={rule.id} className="bg-white border border-slate-200 rounded p-2 mb-2 grid grid-cols-6 gap-2 items-center">
                    <input value={rule.if_variable} onChange={e => { const arr = [...selected.rounds]; const rs = [...r.rules]; rs[rui] = { ...rule, if_variable: e.target.value }; arr[ri] = { ...r, rules: rs }; updateField('rounds', arr); }} placeholder="IF var" className="px-2 py-1 border border-slate-300 rounded text-xs" />
                    <select value={rule.operator} onChange={e => { const arr = [...selected.rounds]; const rs = [...r.rules]; rs[rui] = { ...rule, operator: e.target.value }; arr[ri] = { ...r, rules: rs }; updateField('rounds', arr); }} className="px-2 py-1 border border-slate-300 rounded text-xs">
                      <option value=">">&gt;</option><option value="<">&lt;</option><option value=">=">&gt;=</option><option value="<=">&lt;=</option><option value="==">==</option>
                    </select>
                    <input type="number" value={rule.value} onChange={e => { const arr = [...selected.rounds]; const rs = [...r.rules]; rs[rui] = { ...rule, value: Number(e.target.value) }; arr[ri] = { ...r, rules: rs }; updateField('rounds', arr); }} placeholder="Value" className="px-2 py-1 border border-slate-300 rounded text-xs" />
                    <input value={Object.entries(rule.then_effects || {}).map(([k, v]) => `${k}:${v}`).join(', ')} onChange={e => { const effects = {}; e.target.value.split(',').forEach(pair => { const [k, v] = pair.split(':').map(x => x?.trim()); if (k && v !== undefined && !isNaN(Number(v))) effects[k] = Number(v); }); const arr = [...selected.rounds]; const rs = [...r.rules]; rs[rui] = { ...rule, then_effects: effects }; arr[ri] = { ...r, rules: rs }; updateField('rounds', arr); }} placeholder="THEN e.g. Cash:-5" className="px-2 py-1 border border-slate-300 rounded text-xs col-span-2" />
                    <button onClick={() => { const arr = [...selected.rounds]; const rs = r.rules.filter((_, idx) => idx !== rui); arr[ri] = { ...r, rules: rs }; updateField('rounds', arr); }} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scoring */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-serif font-semibold text-slate-900">Scoring</h4>
          <button onClick={() => updateField('scoring', [...selected.scoring, emptyScoring()])} className="px-3 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add dimension</button>
        </div>
        {selected.scoring.length === 0 && <p className="text-sm text-slate-500">Define scoring dimensions like &quot;Financial Performance&quot; tied to a variable.</p>}
        <div className="space-y-2">
          {selected.scoring.map((sd, si) => (
            <div key={sd.id} className="grid grid-cols-6 gap-2 items-center">
              <input value={sd.name} onChange={e => { const arr = [...selected.scoring]; arr[si] = { ...sd, name: e.target.value }; updateField('scoring', arr); }} placeholder="Dimension" className="px-2 py-1.5 border border-slate-300 rounded text-sm col-span-2" />
              <input value={sd.variable_name} onChange={e => { const arr = [...selected.scoring]; arr[si] = { ...sd, variable_name: e.target.value }; updateField('scoring', arr); }} placeholder="Variable" className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
              <input type="number" value={sd.weight} onChange={e => { const arr = [...selected.scoring]; arr[si] = { ...sd, weight: Number(e.target.value) }; updateField('scoring', arr); }} placeholder="Weight" className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
              <input type="number" value={sd.benchmark} onChange={e => { const arr = [...selected.scoring]; arr[si] = { ...sd, benchmark: Number(e.target.value) }; updateField('scoring', arr); }} placeholder="Benchmark" className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
              <button onClick={() => updateField('scoring', selected.scoring.filter((_, idx) => idx !== si))} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Debrief */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="font-serif font-semibold text-slate-900 mb-3">Debrief</h4>
        <textarea rows={5} value={selected.debrief} onChange={e => updateField('debrief', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="Final learning takeaways shown to learners after completion..." />
      </div>
    </div>
  );
}
